import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { api } from "../api";
import { PageSection, ReportTable, StatusBadge } from "../components/reporting";
import { useSession } from "../session";

interface FmcAdministrator {
  id: string;
  name: string;
  email: string;
  role: "admin" | "super_admin";
  emailVerified: boolean;
  createdAt: string;
  lastActive: string | null;
  isCurrent: boolean;
}

type MappingTarget =
  | "objectId"
  | "sourceModifiedAt"
  | "capturedAt"
  | "firstName"
  | "lastName"
  | "email"
  | "phone"
  | "jobTitle"
  | "practice"
  | "practicePostcode"
  | "practiceAddress"
  | "practiceType"
  | "enquiryNotes"
  | "status"
  | "campaign"
  | "contentAction"
  | "location"
  | "sector"
  | "interests"
  | "marketingOptIn"
  | "consentCapturedAt"
  | "consentEvidence";

interface MappingRow {
  target: MappingTarget;
  source: string;
}

interface MappingIssue {
  field: string;
  message: string;
}

interface MappingSettings {
  mapping: MappingRow[];
  version: number;
  updatedAt: string;
  testEndpoint: {
    url: string;
    apiKeyHeader: string;
    createdAt: string;
  } | null;
  latestSample: {
    id: string;
    fields: { path: string; type: string }[];
    mappingVersion: number;
    valid: boolean;
    issues: MappingIssue[];
    receivedAt: string;
    expiresAt: string;
  } | null;
}

const mappingDefinitions: Record<
  MappingTarget,
  { label: string; required: boolean; group: "identity" | "lead" | "consent" }
> = {
  objectId: { label: "HubSpot object ID", required: false, group: "identity" },
  sourceModifiedAt: { label: "Source modified at", required: false, group: "identity" },
  capturedAt: { label: "Lead created at", required: false, group: "identity" },
  firstName: { label: "First name", required: false, group: "lead" },
  lastName: { label: "Last name", required: false, group: "lead" },
  email: { label: "Email", required: true, group: "lead" },
  phone: { label: "Phone", required: false, group: "lead" },
  jobTitle: { label: "Job title", required: false, group: "lead" },
  practice: { label: "Practice Name", required: false, group: "lead" },
  practicePostcode: { label: "Practice Postcode", required: false, group: "lead" },
  practiceAddress: { label: "Practice Address", required: false, group: "lead" },
  practiceType: { label: "Practice Type", required: false, group: "lead" },
  enquiryNotes: { label: "Notes", required: false, group: "lead" },
  status: { label: "MQL / SQL status", required: true, group: "lead" },
  campaign: { label: "Campaign", required: false, group: "lead" },
  contentAction: { label: "Content action", required: false, group: "lead" },
  location: { label: "Location", required: false, group: "lead" },
  sector: { label: "Sector", required: false, group: "lead" },
  interests: { label: "Interests", required: false, group: "lead" },
  marketingOptIn: { label: "Marketing opt-in", required: false, group: "consent" },
  consentCapturedAt: { label: "Consent captured at", required: false, group: "consent" },
  consentEvidence: { label: "Consent evidence", required: false, group: "consent" },
};

const visibleHubspotTargets = new Set<MappingTarget>([
  "capturedAt",
  "email",
  "phone",
  "lastName",
  "firstName",
  "status",
  "practice",
  "practicePostcode",
  "practiceAddress",
  "practiceType",
  "enquiryNotes",
]);

const inputClass =
  "w-full h-9 px-3 rounded-xs border border-default bg-surface-white text-primary placeholder:text-tertiary font-mono text-caption focus:outline-none focus:border-brand-red/50 transition-colors";
const teamInputClass =
  "w-full h-10 px-3 rounded-xs border border-default bg-surface-white text-primary placeholder:text-tertiary text-body focus:outline-none focus:border-brand-red/50 transition-colors";

function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={htmlFor} className="text-caption font-medium text-primary">
          {label}
        </label>
        {hint && <span className="text-micro text-tertiary">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

export function AdminSettings() {
  const session = useSession();
  const [settings, setSettings] = useState<MappingSettings | null>(null);
  const [mapping, setMapping] = useState<MappingRow[]>([]);
  const [baseline, setBaseline] = useState<MappingRow[]>([]);
  const [credentials, setCredentials] = useState<{
    url: string;
    apiKey: string;
    apiKeyHeader: string;
  } | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [administrators, setAdministrators] = useState<FmcAdministrator[]>([]);
  const [administratorName, setAdministratorName] = useState("");
  const [administratorEmail, setAdministratorEmail] = useState("");
  const [teamBusy, setTeamBusy] = useState(false);
  const [teamNotice, setTeamNotice] = useState("");
  const [resendingAdministratorId, setResendingAdministratorId] = useState<string | null>(null);
  const [removingAdministrator, setRemovingAdministrator] = useState<FmcAdministrator | null>(null);
  const [removalSaving, setRemovalSaving] = useState(false);
  const [removalError, setRemovalError] = useState("");

  const loadSettings = async () => {
    const next = await api<MappingSettings>("/api/admin/hubspot-mapping");
    setSettings(next);
    setMapping(next.mapping);
    setBaseline(next.mapping);
    return next;
  };

  const loadAdministrators = async () => {
    const result = await api<{ items: FmcAdministrator[] }>("/api/admin/team");
    setAdministrators(result.items);
  };

  useEffect(() => {
    loadSettings().catch((problem) =>
      setError(problem instanceof Error ? problem.message : "HubSpot mapping is unavailable"),
    );
    loadAdministrators().catch((problem) =>
      setError(problem instanceof Error ? problem.message : "FMC team is unavailable"),
    );
  }, []);

  useEffect(() => {
    if (!confirmOpen && !removingAdministrator) return undefined;
    const close = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setConfirmOpen(false);
      if (!removalSaving) setRemovingAdministrator(null);
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [confirmOpen, removalSaving, removingAdministrator]);

  const changes = useMemo(
    () =>
      mapping.filter(
        (row) => baseline.find((original) => original.target === row.target)?.source !== row.source,
      ),
    [baseline, mapping],
  );

  const mappingHasRequiredValues = mapping.every(
    (row) => !mappingDefinitions[row.target].required || Boolean(row.source.trim()),
  );

  const rotateTestEndpoint = async () => {
    const password = window.prompt("Confirm your FMC admin password to rotate the test endpoint.");
    if (!password) return;
    setBusy(true);
    setError("");
    try {
      const reauthentication = await api<{ token: string }>("/api/reauthenticate", {
        method: "POST",
        body: JSON.stringify({ password, action: "secret.rotate" }),
      });
      const created = await api<{
        url: string;
        apiKey: string;
        apiKeyHeader: string;
      }>("/api/admin/hubspot-test-endpoint/rotate", {
        method: "POST",
        body: JSON.stringify({ reauthenticationToken: reauthentication.token }),
      });
      setCredentials(created);
      await loadSettings();
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : "Test endpoint could not be rotated");
    } finally {
      setBusy(false);
    }
  };

  const applyMapping = async () => {
    if (!settings || !mappingHasRequiredValues) return;
    const password = window.prompt("Confirm your FMC admin password to apply this mapping.");
    if (!password) return;
    setBusy(true);
    setError("");
    try {
      const reauthentication = await api<{ token: string }>("/api/reauthenticate", {
        method: "POST",
        body: JSON.stringify({ password, action: "mapping.change" }),
      });
      const updated = await api<{
        mapping: MappingRow[];
        version: number;
        updatedAt: string;
      }>("/api/admin/hubspot-mapping", {
        method: "PUT",
        body: JSON.stringify({
          version: settings.version,
          mapping,
          reauthenticationToken: reauthentication.token,
        }),
      });
      setSettings((current) => (current ? { ...current, ...updated } : current));
      setMapping(updated.mapping);
      setBaseline(updated.mapping);
      setConfirmOpen(false);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 3_000);
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : "Mapping could not be applied");
    } finally {
      setBusy(false);
    }
  };

  const refreshSample = async () => {
    setBusy(true);
    setError("");
    try {
      await loadSettings();
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : "Test result could not be refreshed");
    } finally {
      setBusy(false);
    }
  };

  const inviteAdministrator = async (event: React.FormEvent) => {
    event.preventDefault();
    setTeamBusy(true);
    setError("");
    setTeamNotice("");
    try {
      const result = await api<{ setupEmail: "sent" | "not_required" | "failed" }>(
        "/api/admin/team",
        {
          method: "POST",
          body: JSON.stringify({ name: administratorName, email: administratorEmail }),
        },
      );
      setAdministratorName("");
      setAdministratorEmail("");
      await loadAdministrators();
      setTeamNotice(
        result.setupEmail === "failed"
          ? "Admin access was created, but the setup email could not be sent. Use Resend setup below."
          : result.setupEmail === "sent"
            ? "Admin added and a 24-hour account setup email has been sent."
            : "Admin access has been added to the existing active account.",
      );
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : "FMC Admin could not be added");
    } finally {
      setTeamBusy(false);
    }
  };

  const resendAdministratorInvite = async (administrator: FmcAdministrator) => {
    setResendingAdministratorId(administrator.id);
    setError("");
    setTeamNotice("");
    try {
      await api(`/api/admin/team/${encodeURIComponent(administrator.id)}/resend-invite`, {
        method: "POST",
      });
      setTeamNotice(`A fresh 24-hour setup email has been sent to ${administrator.name}.`);
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : "The setup email could not be resent");
    } finally {
      setResendingAdministratorId(null);
    }
  };

  const removeAdministrator = async () => {
    if (!removingAdministrator) return;
    setRemovalSaving(true);
    setRemovalError("");
    setTeamNotice("");
    try {
      await api(`/api/admin/team/${encodeURIComponent(removingAdministrator.id)}`, {
        method: "DELETE",
      });
      const removedName = removingAdministrator.name;
      await loadAdministrators();
      setRemovingAdministrator(null);
      setTeamNotice(
        `${removedName}'s platform access has been removed. They can be added again later if needed.`,
      );
    } catch (problem) {
      setRemovalError(
        problem instanceof Error
          ? problem.message
          : "FMC administrator access could not be removed",
      );
    } finally {
      setRemovalSaving(false);
    }
  };

  const sample = settings?.latestSample ?? null;
  const sampleUsesCurrentMapping = Boolean(sample && sample.mappingVersion === settings?.version);
  const capturedPaths = sample?.fields.map((field) => field.path) ?? [];

  return (
    <div className="flex flex-col gap-[24px]">
      {error && (
        <div role="alert" className="rounded-md border border-status-red/30 bg-brand-red-tint p-4">
          <p className="text-body text-status-red">{error}</p>
        </div>
      )}

      {teamNotice && (
        <div
          role="status"
          className="rounded-md border border-status-green/30 bg-[rgba(41,166,118,0.1)] p-4"
        >
          <p className="text-body text-primary">{teamNotice}</p>
        </div>
      )}

      <PageSection
        title="FMC team"
        right={
          <StatusBadge
            label={session.fmcAdminRole === "super_admin" ? "Super Admin" : "Admin"}
            tone={session.fmcAdminRole === "super_admin" ? "blue" : "neutral"}
          />
        }
      >
        <div className="flex flex-col gap-6">
          {session.fmcAdminRole === "super_admin" ? (
            <form
              onSubmit={inviteAdministrator}
              className="rounded-md border border-default bg-surface-light p-4"
            >
              <div className="mb-4">
                <h3 className="text-body font-semibold text-primary">Add an FMC Admin</h3>
                <p className="text-caption text-tertiary mt-0.5">
                  Admins can administer every brand and platform setting. Only a Super Admin can add
                  another FMC Admin.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-3 items-end">
                <Field label="Full name" htmlFor="fmc-admin-name">
                  <input
                    id="fmc-admin-name"
                    value={administratorName}
                    onChange={(event) => setAdministratorName(event.target.value)}
                    placeholder="e.g. Hannah Cole"
                    autoComplete="off"
                    required
                    className={teamInputClass}
                  />
                </Field>
                <Field label="FMC email" htmlFor="fmc-admin-email" hint="@fmc.co.uk">
                  <input
                    id="fmc-admin-email"
                    type="email"
                    value={administratorEmail}
                    onChange={(event) => setAdministratorEmail(event.target.value)}
                    placeholder="name@fmc.co.uk"
                    autoComplete="off"
                    required
                    pattern=".+@fmc[.]co[.]uk"
                    className={teamInputClass}
                  />
                </Field>
                <button
                  type="submit"
                  disabled={teamBusy}
                  className="h-10 px-4 rounded-xs bg-brand-red text-on-brand text-body font-semibold inline-flex items-center justify-center gap-2 hover:bg-brand-red-hover disabled:opacity-50"
                >
                  <i className="fa-solid fa-paper-plane text-[12px]" aria-hidden="true" />
                  {teamBusy ? "Adding…" : "Add Admin"}
                </button>
              </div>
            </form>
          ) : (
            <div className="rounded-md border border-default bg-surface-light px-4 py-3">
              <p className="text-body text-secondary">
                You can administer the platform and every brand. A Super Admin manages access for
                additional FMC administrators.
              </p>
            </div>
          )}

          <ReportTable
            columns={[
              { label: "Team member", width: "30%" },
              { label: "Platform access", width: "18%" },
              { label: "Last active", width: "18%" },
              { label: "Status", width: "14%" },
              { label: "Actions", align: "right", width: "20%" },
            ]}
            rows={administrators.map((administrator) => [
              <div key={`${administrator.id}-identity`}>
                <div className="font-medium text-primary">{administrator.name}</div>
                <div className="text-caption text-tertiary">{administrator.email}</div>
              </div>,
              <StatusBadge
                key={`${administrator.id}-role`}
                label={administrator.role === "super_admin" ? "Super Admin" : "Admin"}
                tone={administrator.role === "super_admin" ? "blue" : "neutral"}
              />,
              <span key={`${administrator.id}-active`} className="whitespace-nowrap">
                {administrator.lastActive
                  ? new Date(administrator.lastActive).toLocaleString("en-GB")
                  : "Never"}
              </span>,
              <StatusBadge
                key={`${administrator.id}-status`}
                label={administrator.emailVerified ? "Active" : "Setup pending"}
                tone={administrator.emailVerified ? "green" : "amber"}
              />,
              <div
                key={`${administrator.id}-action`}
                className="flex items-center justify-end gap-2"
              >
                {session.fmcAdminRole === "super_admin" && !administrator.isCurrent ? (
                  <>
                    {!administrator.emailVerified && (
                      <button
                        type="button"
                        onClick={() => void resendAdministratorInvite(administrator)}
                        disabled={resendingAdministratorId === administrator.id || removalSaving}
                        className="whitespace-nowrap h-8 px-3 rounded-xs border border-default bg-surface-white text-caption font-medium text-primary hover:bg-surface-light disabled:opacity-50 inline-flex items-center justify-center gap-2"
                        aria-label={`Resend account setup to ${administrator.name}`}
                      >
                        <i className="fa-solid fa-paper-plane text-[11px]" aria-hidden="true" />
                        {resendingAdministratorId === administrator.id
                          ? "Sending…"
                          : "Resend setup"}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setRemovalError("");
                        setRemovingAdministrator(administrator);
                      }}
                      disabled={removalSaving || resendingAdministratorId !== null}
                      className="whitespace-nowrap h-8 px-3 rounded-xs border border-default bg-surface-white text-caption font-medium text-status-red hover:bg-[rgba(255,101,80,0.08)] hover:border-status-red/30 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                      aria-label={`Remove ${administrator.name}'s platform access`}
                    >
                      <i className="fa-solid fa-trash-can text-[11px]" aria-hidden="true" />
                      Remove
                    </button>
                  </>
                ) : administrator.isCurrent ? (
                  <span className="text-caption text-tertiary whitespace-nowrap">
                    Current account
                  </span>
                ) : null}
              </div>,
            ])}
          />
          {administrators.length === 0 && (
            <p className="text-body text-tertiary">No FMC administrators are available.</p>
          )}
        </div>
      </PageSection>

      <PageSection title="Lead intake" right={<StatusBadge label="Authenticated" tone="green" />}>
        <div className="flex items-start gap-4">
          <span className="w-11 h-11 shrink-0 rounded-md bg-surface-light border border-default flex items-center justify-center">
            <i className="fa-brands fa-hubspot text-[20px]" style={{ color: "#FF7A59" }} />
          </span>
          <div className="flex-1">
            <div className="text-body font-semibold text-primary">FMC HubSpot automations</div>
            <p className="text-caption text-tertiary mt-0.5 max-w-[720px]">
              Every client has its own production URL and key. The platform mapping below converts
              configured HubSpot properties into the fixed Leads Manager contract before tenant data
              is stored.
            </p>
          </div>
        </div>
      </PageSection>

      <PageSection
        title="Test webhook endpoint"
        right={
          <button
            type="button"
            onClick={rotateTestEndpoint}
            disabled={busy}
            className="h-9 px-3.5 rounded-xs border border-default text-button text-body font-medium flex items-center gap-2 hover:bg-surface-light transition-colors disabled:opacity-50"
          >
            <i className="fa-solid fa-rotate text-[12px]" />
            {settings?.testEndpoint ? "Rotate endpoint" : "Create endpoint"}
          </button>
        }
      >
        <div className="flex flex-col gap-5 max-w-[860px]">
          <div>
            <p className="text-body text-secondary">
              Point a HubSpot test webhook at this endpoint to discover property paths and check the
              current mapping. It never creates a lead or triggers a destination.
            </p>
            <p className="text-caption text-tertiary mt-1">
              Submitted values are discarded immediately. Only property paths, data types and
              validation issues are retained for one hour.
            </p>
          </div>

          {settings?.testEndpoint ? (
            <div className="grid grid-cols-1 gap-3">
              <Field label="Test endpoint URL" htmlFor="hubspot-test-url">
                <input
                  id="hubspot-test-url"
                  className={inputClass}
                  readOnly
                  value={settings.testEndpoint.url}
                />
              </Field>
              <Field label="API key header" htmlFor="hubspot-test-header">
                <input
                  id="hubspot-test-header"
                  className={inputClass}
                  readOnly
                  value={settings.testEndpoint.apiKeyHeader}
                />
              </Field>
            </div>
          ) : (
            <div className="rounded-md border border-default bg-surface-light px-4 py-3">
              <p className="text-body text-secondary">
                Create a test endpoint before sending a sample payload.
              </p>
            </div>
          )}

          {credentials && (
            <div
              role="status"
              className="rounded-md border border-status-blue/30 bg-surface-info/40 p-4"
            >
              <div className="flex items-center gap-2 mb-3">
                <i className="fa-solid fa-key text-brand-dark-teal" />
                <p className="text-body font-semibold text-primary">Copy the API key now</p>
              </div>
              <p className="text-caption text-secondary mb-3">
                This key is shown once. Configure it as the {credentials.apiKeyHeader} request
                header in HubSpot.
              </p>
              <input
                aria-label="One-time test webhook API key"
                className={inputClass}
                readOnly
                value={credentials.apiKey}
              />
            </div>
          )}

          <div className="border-t border-default pt-5">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div>
                <h3 className="text-body font-semibold text-primary">Latest payload structure</h3>
                <p className="text-caption text-tertiary mt-0.5">
                  Refresh after HubSpot reports a successful test request.
                </p>
              </div>
              <button
                type="button"
                onClick={refreshSample}
                disabled={busy}
                className="h-9 px-3.5 rounded-xs border border-default text-body font-medium hover:bg-surface-light transition-colors disabled:opacity-50"
              >
                Refresh result
              </button>
            </div>
            {sample ? (
              <div className="rounded-md border border-default overflow-hidden">
                <div className="flex items-center justify-between gap-3 bg-surface-light px-4 py-3 border-b border-default">
                  <span className="text-caption text-secondary">
                    Received {new Date(sample.receivedAt).toLocaleString("en-GB")}
                  </span>
                  <StatusBadge
                    label={
                      !sampleUsesCurrentMapping
                        ? "Retest current mapping"
                        : sample.valid
                          ? "Mapping valid"
                          : "Mapping needs attention"
                    }
                    tone={sampleUsesCurrentMapping && sample.valid ? "green" : "amber"}
                  />
                </div>
                {!sampleUsesCurrentMapping && (
                  <div className="border-b border-default bg-[rgba(255,184,53,0.08)] px-4 py-2.5">
                    <p className="text-caption text-secondary">
                      This result used mapping version {sample.mappingVersion}. Send the payload
                      again to test version {settings?.version}.
                    </p>
                  </div>
                )}
                <div className="grid grid-cols-[minmax(0,1fr)_140px] gap-3 px-4 py-2 text-micro font-semibold uppercase tracking-[0.06em] text-tertiary">
                  <span>Captured property path</span>
                  <span>Type</span>
                </div>
                <div className="max-h-[280px] overflow-y-auto scroll-thin divide-y divide-[var(--border-default)]">
                  {sample.fields.map((field) => (
                    <div
                      key={field.path}
                      className="grid grid-cols-[minmax(0,1fr)_140px] gap-3 px-4 py-2.5"
                    >
                      <code className="text-caption text-primary break-all">{field.path}</code>
                      <span className="text-caption text-tertiary">{field.type}</span>
                    </div>
                  ))}
                </div>
                {sample.issues.length > 0 && (
                  <div className="border-t border-default px-4 py-3 bg-[rgba(255,184,53,0.08)]">
                    <p className="text-caption font-semibold text-primary mb-2">
                      Validation issues
                    </p>
                    <ul className="flex flex-col gap-1">
                      {sample.issues.map((issue, index) => (
                        <li key={`${issue.field}-${index}`} className="text-caption text-secondary">
                          <code className="text-primary">{issue.field}</code>: {issue.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-body text-tertiary">No test payload has been received yet.</p>
            )}
          </div>
        </div>
      </PageSection>

      <PageSection
        title="Default field mapping"
        right={
          saved ? (
            <StatusBadge label="Mapping applied" tone="green" />
          ) : (
            <StatusBadge
              label={`Version ${settings?.version ?? "—"} · all brands`}
              tone="neutral"
            />
          )
        }
      >
        <div className="flex flex-col gap-4 max-w-[860px]">
          <p className="text-body text-secondary">
            This mapping applies to every brand and matches the eleven fields available from the FMC
            HubSpot workflow. Choose from the latest captured property paths by focusing an input.
          </p>
          <datalist id="captured-hubspot-properties">
            {capturedPaths.map((path) => (
              <option key={path} value={path} />
            ))}
          </datalist>
          <div className="flex flex-col">
            <div className="grid grid-cols-[minmax(0,1fr)_24px_minmax(0,1fr)_84px] items-center gap-3 pb-2 text-micro font-semibold uppercase tracking-[0.06em] text-tertiary">
              <span>HubSpot property</span>
              <span />
              <span>Leads Manager field</span>
              <span>Required</span>
            </div>
            {mapping
              .filter((row) => visibleHubspotTargets.has(row.target))
              .map((row, index) => {
                const definition = mappingDefinitions[row.target];
                return (
                  <div
                    key={row.target}
                    className={`grid grid-cols-[minmax(0,1fr)_24px_minmax(0,1fr)_84px] items-center gap-3 py-3 ${index > 0 ? "border-t border-default" : ""}`}
                  >
                    <input
                      aria-label={`HubSpot property for ${definition.label}`}
                      className={inputClass}
                      list="captured-hubspot-properties"
                      value={row.source}
                      placeholder={definition.required ? "Required property path" : "Not mapped"}
                      onChange={(event) =>
                        setMapping((rows) =>
                          rows.map((item) =>
                            item.target === row.target
                              ? { ...item, source: event.target.value }
                              : item,
                          ),
                        )
                      }
                    />
                    <i className="fa-solid fa-arrow-right text-[11px] text-tertiary" />
                    <span className="font-mono text-caption text-primary">{definition.label}</span>
                    <span
                      className={`text-micro font-medium ${definition.required ? "text-primary" : "text-tertiary"}`}
                    >
                      {definition.required ? "Required" : "Optional"}
                    </span>
                  </div>
                );
              })}
          </div>
          {!mappingHasRequiredValues && (
            <p role="alert" className="text-caption text-status-red">
              Email and lead status must remain mapped.
            </p>
          )}
          <div className="flex items-center justify-between gap-3 border-t border-default pt-4">
            <p className="text-caption text-tertiary">
              Unknown properties and raw payload values are never stored on a lead.
            </p>
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              disabled={busy || changes.length === 0 || !mappingHasRequiredValues}
              className="h-9 px-3.5 rounded-xs bg-brand-red text-on-brand text-body font-semibold hover:bg-brand-red-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Save mapping
            </button>
          </div>
        </div>
      </PageSection>

      {removingAdministrator && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center px-5">
          <button
            type="button"
            aria-label="Cancel administrator removal"
            className="absolute inset-0 bg-black/35"
            onClick={() => {
              if (!removalSaving) setRemovingAdministrator(null);
            }}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="remove-administrator-title"
            className="relative w-full max-w-[460px] rounded-xl border border-default bg-surface-white p-6 shadow-[var(--shadow-md)]"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[rgba(255,101,80,0.12)] text-status-red">
              <i className="fa-solid fa-user-minus text-[15px]" aria-hidden="true" />
            </div>
            <h2
              id="remove-administrator-title"
              className="mt-4 text-title-md font-bold text-primary"
            >
              Remove {removingAdministrator.name}?
            </h2>
            <p className="mt-2 text-body text-secondary">
              They will immediately lose platform administration access and any active sessions will
              end. Their account will be retained so a Super Admin can add them again later.
            </p>
            {!removingAdministrator.emailVerified && (
              <p className="mt-2 text-caption text-secondary">
                Their pending account setup links will also be invalidated.
              </p>
            )}
            {removalError && (
              <p role="alert" className="mt-3 text-caption text-status-red">
                {removalError}
              </p>
            )}
            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                type="button"
                disabled={removalSaving}
                onClick={() => setRemovingAdministrator(null)}
                className="h-10 px-4 rounded-xs border border-default bg-surface-white text-body font-medium text-secondary hover:bg-surface-light transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={removalSaving}
                onClick={() => void removeAdministrator()}
                className="h-10 px-4 rounded-xs bg-status-red text-white text-body font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                <i className="fa-solid fa-trash-can text-[11px]" aria-hidden="true" />
                {removalSaving ? "Removing…" : "Remove access"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setConfirmOpen(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="mapping-confirm-title"
            className="w-full max-w-[560px] bg-surface-white border border-default rounded-xl shadow-[var(--shadow-md)]"
          >
            <div className="px-6 pt-6 pb-5">
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 shrink-0 rounded-md bg-brand-red-tint text-status-red flex items-center justify-center">
                  <i className="fa-solid fa-triangle-exclamation text-[17px]" />
                </span>
                <div>
                  <h2
                    id="mapping-confirm-title"
                    className="text-heading font-semibold text-primary"
                  >
                    Apply mapping to all brands?
                  </h2>
                  <p className="text-body text-secondary mt-2">
                    New webhook requests will use this mapping immediately. Existing leads are not
                    reprocessed.
                  </p>
                </div>
              </div>
              <div className="mt-5 rounded-md border border-default divide-y divide-[var(--border-default)] px-3">
                {changes.map((row) => (
                  <div key={row.target} className="flex items-center justify-between gap-3 py-2.5">
                    <span className="text-caption text-primary">
                      {mappingDefinitions[row.target].label}
                    </span>
                    <span className="font-mono text-caption text-secondary truncate max-w-[300px]">
                      {row.source || "Not mapped"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-default px-6 py-4">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="h-9 px-3.5 rounded-xs border border-default text-body font-medium hover:bg-surface-light"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyMapping}
                disabled={busy}
                className="h-9 px-3.5 rounded-xs bg-brand-red text-on-brand text-body font-semibold hover:bg-brand-red-hover disabled:opacity-50"
              >
                Apply to all brands
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
